/**
 * Admin Raffle Edit Page - manage prizes, tickets, and run the raffle
 */

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/layout";
import { PageLoader } from "@/components/PageLoader";
import {
  IconGift,
  IconTicket,
  IconPlus,
  IconTrash,
  IconAlertTriangle,
  IconClock,
  IconExternalLink,
  IconLink,
  IconPlay,
  IconCheck,
  IconChevronLeft,
  IconArrowRight,
  IconEye,
} from "@/components/Icons";
import { getTelegram, haptic } from "@/lib/telegram";
import { config } from "@/config";
import {
  useAdminRaffle,
  useUpdateRaffle,
  useDeleteRaffle,
  useResetTestRaffle,
  useRafflePrizes,
  useRaffleStateMutations,
  useDrawPrize,
  useNextPrize,
  useShowWinner,
  useShowCompleted,
  type RaffleStatus,
} from "./hooks";
import { PrizeList } from "./components/PrizeList";
import { TicketSelector } from "./components/TicketSelector";

const STATUS_CONFIG: Record<
  RaffleStatus,
  { label: string; color: string; nextAction?: string; nextLabel?: string }
> = {
  DRAFT: {
    label: "Черновик",
    color: "bg-slate-100 text-slate-600",
    nextAction: "ready",
    nextLabel: "Готово к запуску",
  },
  READY: {
    label: "Готов к запуску",
    color: "bg-blue-100 text-blue-600",
    nextAction: "start",
    nextLabel: "Начать розыгрыш",
  },
  IN_PROGRESS: {
    label: "Идёт розыгрыш",
    color: "bg-amber-100 text-amber-600",
    nextAction: "complete",
    nextLabel: "Завершить",
  },
  COMPLETED: {
    label: "Завершён",
    color: "bg-green-100 text-green-600",
  },
};

type Tab = "prizes" | "tickets" | "control" | "settings";

export function AdminRaffleEditPage() {
  const { raffleId } = useParams<{ raffleId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  const { raffle, isLoading, error, refetch } = useAdminRaffle(raffleId ?? "");
  const { updateRaffle } = useUpdateRaffle();
  const { deleteRaffle, isLoading: isDeleting } = useDeleteRaffle();
  const { resetTestRaffle, isLoading: isResetting } = useResetTestRaffle();
  const { addPrize, updatePrize, deletePrize, reorderPrizes } = useRafflePrizes();
  const { readyRaffle, unreadyRaffle, startRaffle, completeRaffle, isLoading: isStateChanging } =
    useRaffleStateMutations();
  const { drawPrize, isLoading: isDrawing } = useDrawPrize();
  const { nextPrize, isLoading: isTransitioning } = useNextPrize();
  const { showWinner, isLoading: isShowingWinner } = useShowWinner();
  const { showCompleted, isLoading: isShowingCompleted } = useShowCompleted();

  const [activeTab, setActiveTab] = useState<Tab>("prizes");
  // Track which prize was just drawn (roller stopped)
  const [justDrawnPrizeId, setJustDrawnPrizeId] = useState<string | null>(null);
  // Track if winner was shown on broadcast (after "Показать победителя" clicked)
  const [winnerShownForPrizeId, setWinnerShownForPrizeId] = useState<string | null>(null);
  // Track if final screen was shown on broadcast
  const [finalScreenShown, setFinalScreenShown] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isTest, setIsTest] = useState(false);
  
  // New prize form state
  const [showAddPrizeForm, setShowAddPrizeForm] = useState(false);
  const [newPrizeName, setNewPrizeName] = useState("");
  const [newPrizeDescription, setNewPrizeDescription] = useState("");
  const [isAddingPrize, setIsAddingPrize] = useState(false);

  // Load raffle data into form
  useEffect(() => {
    if (raffle) {
      setName(raffle.name);
      setDescription(raffle.description ?? "");
      setIsTest(raffle.isTest);
    }
  }, [raffle]);

  // BackButton handling
  useEffect(() => {
    if (!tg || !raffle) return;

    tg.BackButton.show();
    const handleBack = () => navigate(`/admin/raffle/event/${raffle.eventId}`);
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, raffle]);

  const handleSave = async () => {
    if (!raffleId) return;
    try {
      await updateRaffle({ id: raffleId, name, description, isTest });
      haptic("success");
      refetch();
    } catch (err) {
      haptic("error");
    }
  };

  const handleDelete = async () => {
    if (!raffle || raffle.status !== "DRAFT") return;

    if (!confirm("Удалить розыгрыш?")) return;

    try {
      await deleteRaffle({ id: raffle.id, eventId: raffle.eventId });
      haptic("success");
      navigate(`/admin/raffle/event/${raffle.eventId}`);
    } catch (err) {
      haptic("error");
    }
  };

  const handleStateChange = async () => {
    if (!raffle) return;

    try {
      switch (raffle.status) {
        case "DRAFT":
          await readyRaffle(raffle.id);
          break;
        case "READY":
          await startRaffle(raffle.id);
          break;
        case "IN_PROGRESS":
          await completeRaffle(raffle.id);
          break;
      }
      haptic("success");
      refetch();
    } catch (err) {
      haptic("error");
    }
  };

  const handleBackToDraft = async () => {
    if (!raffle || raffle.status !== "READY") return;

    try {
      await unreadyRaffle(raffle.id);
      haptic("success");
      refetch();
    } catch (err) {
      haptic("error");
    }
  };

  const handleAddPrize = async () => {
    if (!raffleId || !newPrizeName.trim()) return;

    setIsAddingPrize(true);
    try {
      await addPrize({
        raffleId,
        name: newPrizeName.trim(),
        description: newPrizeDescription.trim() || undefined,
      });
      haptic("success");
      setNewPrizeName("");
      setNewPrizeDescription("");
      setShowAddPrizeForm(false);
      refetch();
    } catch (err) {
      haptic("error");
    } finally {
      setIsAddingPrize(false);
    }
  };

  const handleUpdatePrize = async (args: { id: string; name: string; description?: string }) => {
    await updatePrize(args);
    refetch();
  };

  const handleDeletePrize = async (prizeId: string) => {
    if (!raffleId) return;

    if (!confirm("Удалить приз?")) return;

    try {
      await deletePrize({ id: prizeId, raffleId });
      haptic("success");
      refetch();
    } catch (err) {
      haptic("error");
    }
  };


  const handleOpenScreen = () => {
    if (!raffleId) return;
    // Open raffle screen in new window for projection
    window.open(`/raffle/${raffleId}/screen`, "_blank");
  };

  const handleCopyDeepLink = async () => {
    if (!raffleId) return;
    const deepLink = config.raffleDeepLink(raffleId);
    try {
      await navigator.clipboard.writeText(deepLink);
      haptic("success");
      alert("Ссылка скопирована!");
    } catch {
      haptic("error");
      // Fallback: show the link
      prompt("Скопируйте ссылку:", deepLink);
    }
  };

  const handleDrawPrize = async (prizeId: string) => {
    if (!raffleId) return;
    
    try {
      const result = await drawPrize(raffleId, prizeId);
      if (result) {
        haptic("success");
        setJustDrawnPrizeId(prizeId); // Roller stopped, show "Показать победителя" button
        setWinnerShownForPrizeId(null); // Reset winner shown state
        refetch();
      }
    } catch (err) {
      haptic("error");
    }
  };

  const handleShowWinner = async (prizeId: string) => {
    if (!raffleId) return;
    
    try {
      await showWinner(raffleId, prizeId);
      haptic("success");
      setWinnerShownForPrizeId(prizeId); // Winner shown, now show "Далее" button
    } catch (err) {
      haptic("error");
    }
  };

  const handleNextPrize = async () => {
    if (!raffleId) return;
    
    try {
      await nextPrize(raffleId);
      haptic("success");
      setJustDrawnPrizeId(null); // Clear the transition state
      setWinnerShownForPrizeId(null);
      refetch();
    } catch (err) {
      haptic("error");
    }
  };

  const handleShowFinalScreen = async () => {
    if (!raffleId) return;
    
    try {
      await showCompleted(raffleId);
      haptic("success");
      setFinalScreenShown(true);
    } catch (err) {
      haptic("error");
    }
  };

  const handleResetTestRaffle = async () => {
    if (!raffle || !raffle.isTest) return;
    
    if (!confirm("Сбросить тестовый розыгрыш? Все результаты будут удалены и розыгрыш вернётся в состояние «Готово».")) {
      return;
    }
    
    try {
      await resetTestRaffle(raffle.id);
      haptic("success");
      // Reset local state
      setJustDrawnPrizeId(null);
      setWinnerShownForPrizeId(null);
      setFinalScreenShown(false);
      refetch();
    } catch (err) {
      haptic("error");
    }
  };

  if (isLoading) {
    return <PageLoader message="Загрузка розыгрыша..." />;
  }

  if (error || !raffle) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            {error ?? "Розыгрыш не найден"}
          </p>
          <button onClick={() => refetch()} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  const statusConfig = STATUS_CONFIG[raffle.status];
  const canEdit = raffle.status === "DRAFT" || raffle.status === "READY";
  const canRun = raffle.status === "READY" || raffle.status === "IN_PROGRESS";

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header with status */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">{raffle.name}</h1>
              {raffle.isTest && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-600">
                  Тест
                </span>
              )}
            </div>
            <span
              className={`px-3 py-1 rounded-lg text-sm font-medium ${statusConfig.color}`}
            >
              {statusConfig.label}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <span className="flex items-center gap-1">
              <IconGift className="w-4 h-4" />
              {raffle.prizes.length} призов
            </span>
            <span className="flex items-center gap-1">
              <IconTicket className="w-4 h-4" />
              {raffle.ticketCount} билетов
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {raffle.status === "DRAFT" && (
              <button
                onClick={handleStateChange}
                disabled={isStateChanging || raffle.prizes.length === 0 || raffle.ticketCount === 0}
                className="btn-primary flex items-center gap-2"
                title={raffle.prizes.length === 0 ? "Добавьте призы" : raffle.ticketCount === 0 ? "Добавьте билеты" : ""}
              >
                <IconCheck className="w-4 h-4" />
                {isStateChanging ? "Подготовка..." : "Готово к запуску"}
              </button>
            )}
            {canRun && (
              <button
                onClick={() => setActiveTab("control")}
                className={`flex items-center gap-2 ${
                  activeTab === "control" 
                    ? "btn-primary" 
                    : "btn-secondary"
                }`}
              >
                <IconPlay className="w-4 h-4" />
                Управление
              </button>
            )}
            {canRun && (
              <button
                onClick={handleOpenScreen}
                className="btn-secondary flex items-center gap-2"
              >
                <IconExternalLink className="w-4 h-4" />
                Экран
              </button>
            )}
            <button
              onClick={handleCopyDeepLink}
              className="btn-secondary flex items-center gap-2 px-4"
              title="Копировать ссылку для пользователей"
            >
              <IconLink className="w-4 h-4" />
              <span className="hidden sm:inline">Ссылка</span>
            </button>
          </div>
        </div>

        {/* Tabs - hidden when in control mode */}
        {/* Tabs - in control mode only show Settings tab */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {activeTab !== "control" && (
            <>
              <TabButton
                active={activeTab === "prizes"}
                onClick={() => setActiveTab("prizes")}
              >
                <IconGift className="w-4 h-4" />
                Призы
              </TabButton>
              <TabButton
                active={activeTab === "tickets"}
                onClick={() => setActiveTab("tickets")}
              >
                <IconTicket className="w-4 h-4" />
                Билеты
              </TabButton>
            </>
          )}
          <TabButton
            active={activeTab === "settings"}
            onClick={() => setActiveTab("settings")}
          >
            <IconClock className="w-4 h-4" />
            Настройки
          </TabButton>
        </div>

        {/* Tab content */}
        {activeTab === "prizes" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold text-slate-700">Призы</h2>
              {canEdit && !showAddPrizeForm && (
                <button
                  onClick={() => setShowAddPrizeForm(true)}
                  className="btn-secondary flex items-center gap-1 text-sm"
                >
                  <IconPlus className="w-4 h-4" />
                  Добавить
                </button>
              )}
            </div>
            
            {/* Add prize form */}
            {showAddPrizeForm && canEdit && (
              <div className="card p-4 space-y-3 border-2 border-purple-200">
                <input
                  type="text"
                  value={newPrizeName}
                  onChange={(e) => setNewPrizeName(e.target.value)}
                  placeholder="Название приза *"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
                <input
                  type="text"
                  value={newPrizeDescription}
                  onChange={(e) => setNewPrizeDescription(e.target.value)}
                  placeholder="Описание (необязательно)"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleAddPrize}
                    disabled={isAddingPrize || !newPrizeName.trim()}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
                  >
                    {isAddingPrize ? "Добавление..." : "Добавить приз"}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddPrizeForm(false);
                      setNewPrizeName("");
                      setNewPrizeDescription("");
                    }}
                    disabled={isAddingPrize}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            )}
            
            <PrizeList
              prizes={raffle.prizes}
              canEdit={canEdit}
              onDelete={handleDeletePrize}
              onReorder={async (prizeIds) => {
                await reorderPrizes({ raffleId: raffle.id, prizeIds });
                // No refetch needed - PrizeList uses optimistic updates
              }}
              onUpdate={handleUpdatePrize}
            />
          </div>
        )}

        {activeTab === "tickets" && (
          <TicketSelector
            raffleId={raffle.id}
            eventId={raffle.eventId}
            canEdit={canEdit}
            onSave={refetch}
          />
        )}

        {activeTab === "control" && canRun && (
          <div className="space-y-4">
            {/* Control panel header */}
            <div className="card p-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold">Панель управления</h2>
                  <p className="text-sm opacity-80">
                    {raffle.status === "READY" 
                      ? "Нажмите «Начать розыгрыш» чтобы начать"
                      : `Разыграно ${raffle.prizes.filter(p => p.draw).length} из ${raffle.prizes.length} призов`
                    }
                  </p>
                </div>
                <button
                  onClick={handleOpenScreen}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <IconExternalLink className="w-4 h-4" />
                  Открыть экран
                </button>
              </div>
            </div>

            {/* Prize draw controls */}
            {raffle.status === "IN_PROGRESS" && (
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-700">Розыгрыш призов</h3>
                
                {raffle.prizes.map((prize, index) => {
                  const isDrawn = !!prize.draw;
                  const isJustDrawn = isDrawn && prize.id === justDrawnPrizeId;
                  const isWinnerShown = prize.id === winnerShownForPrizeId;
                  const isNext = !isDrawn && raffle.prizes.slice(0, index).every(p => p.draw);
                  const allPrizesDrawn = raffle.prizes.every(p => p.draw);
                  const isLastPrize = allPrizesDrawn && isJustDrawn;
                  // Show "Показать победителя" when prize is just drawn but winner not shown yet
                  const showWinnerButton = isJustDrawn && !isWinnerShown;
                  // Show "Далее" when winner is shown and there are more prizes (not for last prize)
                  const showNextButton = isJustDrawn && isWinnerShown && !allPrizesDrawn;
                  // Show "Показать финальный экран" for last prize after winner is shown
                  const showFinalButton = isLastPrize && isWinnerShown && !finalScreenShown;
                  
                  return (
                    <div
                      key={prize.id}
                      className={`card p-4 flex items-center gap-4 ${
                        isJustDrawn
                          ? "bg-amber-50 border-amber-300 border-2"
                          : isDrawn 
                            ? "bg-green-50 border-green-200" 
                            : isNext 
                              ? "bg-purple-50 border-purple-300 border-2" 
                              : ""
                      }`}
                    >
                      {/* Prize number */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                        isJustDrawn
                          ? "bg-amber-500 text-white"
                          : isDrawn 
                            ? "bg-green-500 text-white" 
                            : isNext 
                              ? "bg-purple-500 text-white" 
                              : "bg-slate-200 text-slate-600"
                      }`}>
                        {isDrawn ? <IconCheck className="w-5 h-5" /> : index + 1}
                      </div>

                      {/* Prize info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{prize.name}</h4>
                        {isDrawn && prize.draw && (
                          <p className={`text-sm ${isJustDrawn ? "text-amber-600" : "text-green-600"}`}>
                            <span className={`font-semibold ${isJustDrawn ? "text-amber-700" : "text-green-700"}`}>#{prize.draw.winningTicket.ticketNumber}</span>
                            <span className="mx-1">—</span>
                            {prize.draw.winningTicket.winnerName}
                            {prize.draw.winningTicket.winnerUsername && (
                              <span className={isJustDrawn ? "text-amber-500" : "text-green-500"}> @{prize.draw.winningTicket.winnerUsername}</span>
                            )}
                          </p>
                        )}
                        {!isDrawn && prize.description && (
                          <p className="text-sm text-slate-500 truncate">{prize.description}</p>
                        )}
                      </div>

                      {/* Show Winner button - first step after draw */}
                      {showWinnerButton && (
                        <button
                          onClick={() => handleShowWinner(prize.id)}
                          disabled={isShowingWinner}
                          className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-200"
                        >
                          <IconEye className="w-4 h-4" />
                          {isShowingWinner ? "Показ..." : "Показать победителя"}
                        </button>
                      )}

                      {/* Next button - second step after showing winner */}
                      {showNextButton && (
                        <button
                          onClick={handleNextPrize}
                          disabled={isTransitioning}
                          className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-amber-500 text-white hover:bg-amber-600 shadow-lg shadow-amber-200"
                        >
                          <IconArrowRight className="w-4 h-4" />
                          {isTransitioning ? "Переход..." : "Далее"}
                        </button>
                      )}

                      {/* Final screen button - for last prize after winner is shown */}
                      {showFinalButton && (
                        <button
                          onClick={() => {
                            handleShowFinalScreen();
                            // Reset just drawn state after showing final screen
                            setJustDrawnPrizeId(null);
                            setWinnerShownForPrizeId(null);
                          }}
                          disabled={isShowingCompleted}
                          className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-200"
                        >
                          <IconEye className="w-4 h-4" />
                          {isShowingCompleted ? "Показ..." : "Показать финальный экран"}
                        </button>
                      )}

                      {/* Draw button */}
                      {!isDrawn && (
                        <button
                          onClick={() => handleDrawPrize(prize.id)}
                          disabled={isDrawing || !isNext || justDrawnPrizeId !== null}
                          className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                            isNext && justDrawnPrizeId === null
                              ? "bg-purple-600 text-white hover:bg-purple-700 shadow-lg shadow-purple-200"
                              : "bg-slate-100 text-slate-400 cursor-not-allowed"
                          }`}
                        >
                          <IconPlay className="w-4 h-4" />
                          {isDrawing ? "Розыгрыш..." : "Разыграть"}
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* All prizes drawn */}
                {raffle.prizes.every(p => p.draw) && !justDrawnPrizeId && (
                  <div className="card p-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-center">
                    <div className="text-4xl mb-3">🎉</div>
                    <h3 className="text-xl font-bold mb-2">Все призы разыграны!</h3>
                    <p className="text-sm opacity-80 mb-4">
                      {finalScreenShown 
                        ? "Финальный экран показан. Можете завершить розыгрыш"
                        : "Покажите финальный экран со всеми победителями"
                      }
                    </p>
                    <div className="flex gap-3 justify-center">
                      {!finalScreenShown && (
                        <button
                          onClick={handleShowFinalScreen}
                          disabled={isShowingCompleted}
                          className="px-6 py-2 bg-white text-emerald-600 hover:bg-white/90 rounded-lg font-medium transition-colors flex items-center gap-2"
                        >
                          <IconEye className="w-4 h-4" />
                          {isShowingCompleted ? "Показ..." : "Показать финальный экран"}
                        </button>
                      )}
                      <button
                        onClick={handleStateChange}
                        disabled={isStateChanging}
                        className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors"
                      >
                        Завершить розыгрыш
                      </button>
                      {raffle.isTest && (
                        <button
                          onClick={handleResetTestRaffle}
                          disabled={isResetting}
                          className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                        >
                          {isResetting ? "Сброс..." : "Сбросить тест"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Start raffle prompt */}
            {raffle.status === "READY" && (
              <div className="card p-6 text-center">
                <div className="text-5xl mb-4">🎲</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Розыгрыш готов к запуску
                </h3>
                <p className="text-slate-500 mb-6">
                  {raffle.prizes.length} призов · {raffle.ticketCount} участников
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleOpenScreen}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <IconExternalLink className="w-4 h-4" />
                    Открыть экран
                  </button>
                  <button
                    onClick={handleStateChange}
                    disabled={isStateChanging}
                    className="btn-primary flex items-center gap-2"
                  >
                    <IconPlay className="w-4 h-4" />
                    Начать розыгрыш
                  </button>
                </div>
              </div>
            )}

            {/* Completed raffle - show final screen button */}
            {raffle.status === "COMPLETED" && (
              <div className="card p-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-center">
                <div className="text-4xl mb-3">🏆</div>
                <h3 className="text-xl font-bold mb-2">Розыгрыш завершён</h3>
                <p className="text-sm opacity-80 mb-4">
                  Разыграно {raffle.prizes.length} призов
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleShowFinalScreen}
                    disabled={isShowingCompleted}
                    className="px-6 py-2 bg-white text-emerald-600 hover:bg-white/90 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <IconEye className="w-4 h-4" />
                    {isShowingCompleted ? "Показ..." : "Показать финальный экран"}
                  </button>
                  <button
                    onClick={handleOpenScreen}
                    className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <IconExternalLink className="w-4 h-4" />
                    Открыть экран
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div className="space-y-4">
            <div className="card p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Название
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Описание
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!canEdit}
                  rows={3}
                  className="input w-full"
                />
              </div>

              {/* Test mode toggle */}
              <div className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-700">Тестовый режим</span>
                    {isTest && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-600">
                        Активен
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Тестовый розыгрыш не виден обычным пользователям
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsTest(!isTest)}
                  disabled={!canEdit}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    isTest ? "bg-orange-500" : "bg-slate-200"
                  } ${!canEdit ? "opacity-50 cursor-not-allowed" : ""}`}
                  role="switch"
                  aria-checked={isTest}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isTest ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {canEdit && (
                <button onClick={handleSave} className="btn-primary w-full">
                  Сохранить
                </button>
              )}
            </div>

            {/* Back to draft - for READY status */}
            {raffle.status === "READY" && (
              <button
                onClick={handleBackToDraft}
                disabled={isStateChanging}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                <IconChevronLeft className="w-4 h-4" />
                Вернуть в черновик
              </button>
            )}

            {/* Delete - for DRAFT status */}
            {raffle.status === "DRAFT" && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-danger w-full flex items-center justify-center gap-2"
              >
                <IconTrash className="w-4 h-4" />
                Удалить розыгрыш
              </button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}
